import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DailyIframe from '@daily-co/daily-js';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Video, Phone, FileText, Users, Clock, AlertTriangle, ShieldCheck, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TelehealthRoom() {
  const { appointmentId } = useParams();
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();

  const [appointment, setAppointment] = useState(null);
  const [roomData, setRoomData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [callActive, setCallActive] = useState(false);
  const [joining, setJoining] = useState(false);
  const [notes, setNotes] = useState('');
  const [soapNote, setSoapNote] = useState(null);
  const [generatingSoap, setGeneratingSoap] = useState(false);
  const [attachingAutoSummary, setAttachingAutoSummary] = useState(false);
  const [autoSummaryAttached, setAutoSummaryAttached] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);

  const frameContainerRef = useRef(null);
  const callFrameRef = useRef(null);
  const timerRef = useRef(null);

  const destroyFrame = useCallback(() => {
    if (callFrameRef.current) {
      try { callFrameRef.current.destroy(); } catch (e) { /* noop */ }
      callFrameRef.current = null;
    }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${API}/telehealth/room/${appointmentId}`, getAuthHeader());
        if (cancelled) return;
        setRoomData(response.data);
        setAppointment(response.data.appointment);
      } catch (error) {
        toast.error('Failed to load telehealth room');
        navigate(-1);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; destroyFrame(); };
  }, [appointmentId]);

  const startCall = async () => {
    if (!roomData?.daily_enabled || !roomData?.room_url || !roomData?.token) {
      toast.error('Live video is not configured for this visit.');
      return;
    }
    if (callFrameRef.current) return;
    setJoining(true);
    try {
      const frame = DailyIframe.createFrame(frameContainerRef.current, {
        showLeaveButton: true,
        iframeStyle: { width: '100%', height: '100%', border: '0', borderRadius: '16px' },
      });
      callFrameRef.current = frame;
      frame.on('joined-meeting', () => {
        setCallActive(true);
        setJoining(false);
        timerRef.current = setInterval(() => setElapsedTime((p) => p + 1), 1000);
        toast.success('Connected to your visit');
      });
      frame.on('left-meeting', () => { setCallActive(false); endCall(); });
      frame.on('error', (e) => { console.error('Daily error', e); toast.error('Video connection error'); setJoining(false); });
      await frame.join({ url: roomData.room_url, token: roomData.token });
    } catch (error) {
      console.error('join error', error);
      toast.error('Unable to connect to video call.');
      setJoining(false);
      destroyFrame();
    }
  };

  const endCall = async () => {
    destroyFrame();
    setCallActive(false);
    try {
      await axios.put(`${API}/appointments/${appointmentId}`, { status: 'completed', notes }, getAuthHeader());
      toast.success('Call ended. Visit recorded.');
    } catch (error) {
      console.error('Failed to update appointment:', error);
    }
  };

  const generateSoapNote = async () => {
    setGeneratingSoap(true);
    try {
      const response = await axios.post(`${API}/appointments/${appointmentId}/generate-soap`, {}, getAuthHeader());
      setSoapNote(response.data.soap_note);
      toast.success('SOAP note generated!');
    } catch (error) {
      toast.error('Failed to generate SOAP note');
    } finally {
      setGeneratingSoap(false);
    }
  };

  const attachAutoSummary = async () => {
    setAttachingAutoSummary(true);
    try {
      const response = await axios.post(
        `${API}/telehealth/${appointmentId}/auto-summary`,
        { clinician_notes: notes || undefined },
        getAuthHeader(),
      );
      const note = response.data?.note;
      if (note?.soap) setSoapNote(note.soap);
      setAutoSummaryAttached(true);
      toast.success(
        response.data?.already_attached
          ? 'Summary already attached — review and lock the note.'
          : 'Auto-summary attached to the clinical record. Review before locking.',
      );
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not attach auto-summary');
    } finally {
      setAttachingAutoSummary(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cn-sage-300 animate-spin" />
      </div>
    );
  }

  const dailyReady = !!roomData?.daily_enabled;

  return (
    <div className="min-h-screen bg-slate-900 text-white" data-testid="telehealth-room">
      <div className="flex h-screen">
        {/* Main Video Area */}
        <div className="flex-1 flex flex-col">
          <header className="flex items-center justify-between p-4 border-b border-slate-700">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-cn-primary rounded-xl flex items-center justify-center">
                <Video className="w-5 h-5" />
              </div>
              <div>
                <h1 className="font-semibold font-display !text-white">Telehealth Visit</h1>
                <p className="text-sm text-slate-400 flex items-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-cn-sage-300" /> Secure room · {roomData?.room_id}
                </p>
              </div>
            </div>

            {callActive && (
              <div className="flex items-center gap-2 px-4 py-2 bg-red-500/20 rounded-full" data-testid="call-timer">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                <Clock className="w-4 h-4 text-red-400" />
                <span className="text-red-400 font-mono">{formatTime(elapsedTime)}</span>
              </div>
            )}

            <Button onClick={() => navigate(-1)} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800" data-testid="exit-room-btn">
              Exit Room
            </Button>
          </header>

          {/* Video Container */}
          <div className="flex-1 relative p-4">
            <div className="w-full h-full bg-slate-800 rounded-2xl overflow-hidden relative">
              {/* Daily Prebuilt mounts here */}
              <div ref={frameContainerRef} className="w-full h-full" data-testid="daily-frame-container" style={{ display: callActive || joining ? 'block' : 'none' }} />

              {!callActive && !joining && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center max-w-sm px-6">
                    <div className="w-24 h-24 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Users className="w-12 h-12 text-slate-500" />
                    </div>
                    {dailyReady ? (
                      <>
                        <p className="text-slate-300 font-medium">Ready to connect</p>
                        <p className="text-sm text-slate-500 mt-2">Click “Start Call” to join the secure video room. {user?.role === 'clinician' ? 'You join as the host.' : 'Your clinician will admit you.'}</p>
                      </>
                    ) : (
                      <>
                        <p className="text-amber-300 font-medium">Live video unavailable</p>
                        <p className="text-sm text-slate-500 mt-2">This visit room isn’t configured for live video. Please contact support.</p>
                      </>
                    )}
                  </div>
                </div>
              )}

              {joining && (
                <div className="absolute inset-0 flex items-center justify-center bg-slate-900/70">
                  <div className="text-center">
                    <Loader2 className="w-8 h-8 text-cn-sage-300 animate-spin mx-auto mb-3" />
                    <p className="text-slate-300 text-sm">Connecting to your visit…</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Controls */}
          <div className="p-4 border-t border-slate-700">
            <div className="flex items-center justify-center gap-4">
              {callActive ? (
                <Button onClick={endCall} data-testid="end-call-btn" className="bg-red-600 hover:bg-red-700 text-white rounded-full px-8 h-14 text-lg">
                  <Phone className="w-6 h-6 mr-2 rotate-[135deg]" /> Leave visit
                </Button>
              ) : (
                <Button onClick={startCall} disabled={!dailyReady || joining} data-testid="start-call-btn" className="bg-cn-primary hover:bg-cn-primary/90 text-white rounded-full px-8 h-14 text-lg disabled:opacity-50">
                  {joining ? <Loader2 className="w-6 h-6 mr-2 animate-spin" /> : <Video className="w-6 h-6 mr-2" />}
                  {joining ? 'Connecting…' : 'Start Call'}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="w-96 border-l border-slate-700 flex flex-col bg-slate-800/50">
          <div className="p-4 border-b border-slate-700">
            <h2 className="font-semibold mb-3 font-display !text-white">Visit Information</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-400">Date</span><span>{appointment?.scheduled_date}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Time</span><span>{appointment?.scheduled_time}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Duration</span><span>{appointment?.duration_minutes} min</span></div>
              <div className="flex justify-between">
                <span className="text-slate-400">Status</span>
                <Badge className={callActive ? 'bg-green-500/20 text-green-400' : 'bg-cn-primary/20 text-cn-sage-200'}>
                  {callActive ? 'In Progress' : appointment?.status}
                </Badge>
              </div>
            </div>
          </div>

          <div className="flex-1 p-4 overflow-auto">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">Visit Notes</label>
                <Textarea
                  data-testid="visit-notes"
                  placeholder="Enter notes during the visit..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 min-h-[120px]"
                />
              </div>

              <div className="pt-4 border-t border-slate-700">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-sm !text-white">AI SOAP Note</h3>
                  {user?.role === 'clinician' && (
                    <div className="flex gap-2">
                      <Button onClick={attachAutoSummary} data-testid="attach-auto-summary-btn" disabled={attachingAutoSummary || autoSummaryAttached} size="sm" className="bg-cn-primary hover:bg-cn-primary/90 text-white rounded-full px-4">
                        {attachingAutoSummary ? <Loader2 className="w-4 h-4 animate-spin" /> : autoSummaryAttached ? <><FileText className="w-4 h-4 mr-1" /> Attached</> : <><FileText className="w-4 h-4 mr-1" /> Auto-attach</>}
                      </Button>
                      <Button onClick={generateSoapNote} data-testid="generate-soap-btn" disabled={generatingSoap} size="sm" className="bg-cn-terracotta-600 hover:bg-cn-terracotta-700 text-white rounded-full px-4">
                        {generatingSoap ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileText className="w-4 h-4 mr-1" /> Generate</>}
                      </Button>
                    </div>
                  )}
                </div>

                {soapNote ? (
                  <div className="space-y-3">
                    {['subjective', 'objective', 'assessment', 'plan'].map((section) => (
                      <div key={section} className="bg-slate-700/50 rounded-lg p-3">
                        <p className="text-xs font-medium text-cn-sage-300 uppercase mb-1">{section}</p>
                        <p className="text-sm text-slate-300">{soapNote[section]}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-slate-700/30 rounded-lg p-4 text-center">
                    <FileText className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">
                      {user?.role === 'clinician' ? 'Generate AI-powered SOAP note after the visit' : 'SOAP note will be available after the visit'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="p-4 border-t border-slate-700">
            <Button variant="outline" className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10" data-testid="report-issue-btn">
              <AlertTriangle className="w-4 h-4 mr-2" /> Report Issue
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
